import { Check, Copy, Play, Terminal } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../api/rest";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtNumber,
	Panel,
	Spinner,
} from "../components/flows/ui";
import { PageHeader } from "../components/layout/PageHeader";
import type {
	DiagnosticResult,
	DiagnosticScriptSummary,
	DiagnosticStatement,
} from "../types/diagnostics";

const cell = (value: unknown): string => {
	if (value === null || value === undefined) return "";
	if (value instanceof Date)
		return value.toISOString().slice(0, 19).replace("T", " ");
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};

/** Tab-separated, so a paste lands in a spreadsheet as columns and in a message as a readable block. */
const asText = (result: DiagnosticResult): string => {
	const lines: string[] = [`# ${result.name} (${result.ms} ms)`];
	if (result.error) lines.push(`ERROR: ${result.error}`);

	for (const statement of result.statements) {
		if (statement.error) {
			lines.push("", `ERROR: ${statement.error}`, statement.sql);
			continue;
		}
		if (statement.heading) {
			lines.push("", statement.heading);
			continue;
		}
		if (statement.rowCount === 0) {
			lines.push("", "(no rows)");
			continue;
		}
		lines.push("", statement.columns.join("\t"));
		for (const row of statement.rows) {
			lines.push(statement.columns.map((c) => cell(row[c])).join("\t"));
		}
	}
	return lines.join("\n");
};

const CopyButton = ({ text, label }: { text: string; label: string }) => {
	const [copied, setCopied] = useState(false);
	return (
		<button
			type="button"
			className={btn.ghost}
			onClick={async () => {
				try {
					await navigator.clipboard.writeText(text);
					setCopied(true);
					setTimeout(() => setCopied(false), 1500);
				} catch {
					setCopied(false);
				}
			}}
		>
			{copied ? (
				<Check className="h-4 w-4 text-emerald-600" />
			) : (
				<Copy className="h-4 w-4" />
			)}
			{copied ? "Copied" : label}
		</button>
	);
};

const StatementView = ({ statement }: { statement: DiagnosticStatement }) => {
	if (statement.error) {
		return (
			<div className="rounded-lg border border-danger/30 bg-danger/5 p-3">
				<p className="text-[12px] font-medium text-danger">{statement.error}</p>
				<pre className="mt-2 overflow-x-auto text-[11px] text-slate-500">
					{statement.sql}
				</pre>
			</div>
		);
	}

	if (statement.heading) {
		return (
			<h4 className="border-b border-slate-200 pb-1 pt-2 text-[13px] font-semibold text-slate-700">
				{statement.heading.replace(/^=+\s*|\s*=+$/g, "")}
			</h4>
		);
	}

	if (statement.rowCount === 0) {
		return (
			<p className="text-[12px] text-slate-400">no rows · {statement.ms} ms</p>
		);
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full text-left text-[12px]">
				<thead>
					<tr className="border-b border-slate-200 text-slate-500">
						{statement.columns.map((column) => (
							<th
								key={column}
								className="whitespace-nowrap py-1.5 pr-4 font-medium"
							>
								{column}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{statement.rows.map((row, index) => (
						<tr
							key={`${statement.index}-${index}`}
							className="border-b border-slate-100 text-slate-700"
						>
							{statement.columns.map((column) => (
								<td key={column} className="whitespace-nowrap py-1.5 pr-4">
									{cell(row[column])}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
			<p className="pt-1 text-[11px] text-slate-400">
				{fmtNumber(statement.rowCount)} rows · {statement.ms} ms
			</p>
		</div>
	);
};

export default function Diagnostics() {
	const [scripts, setScripts] = useState<DiagnosticScriptSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [running, setRunning] = useState<string | null>(null);
	const [results, setResults] = useState<Record<string, DiagnosticResult>>({});

	// The catalogue is read off disk and touches no database, so listing on load is free. Running
	// is not: Phase 0 counts across the whole catalogue, and nothing here executes until asked.
	const load = useCallback(async () => {
		setError(null);
		try {
			const data = await fetchJson<{ items: DiagnosticScriptSummary[] }>(
				"/diagnostics",
			);
			setScripts(data.items);
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const runOne = async (slug: string) => {
		setRunning(slug);
		setError(null);
		try {
			const result = await sendJson<DiagnosticResult>(
				`/diagnostics/${slug}/run`,
				"POST",
			);
			setResults((prev) => ({ ...prev, [slug]: result }));
		} catch (e) {
			setError(String(e));
		} finally {
			setRunning(null);
		}
	};

	const runAll = async () => {
		setRunning("*");
		setError(null);
		try {
			const data = await sendJson<{ results: DiagnosticResult[] }>(
				"/diagnostics/run",
				"POST",
				{},
			);
			const next: Record<string, DiagnosticResult> = {};
			for (const result of data.results) next[result.slug] = result;
			setResults(next);
		} catch (e) {
			setError(String(e));
		} finally {
			setRunning(null);
		}
	};

	const ran = Object.values(results);
	const everything = ran.map(asText).join("\n\n");

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Monitoring · Diagnostics"
				title="Diagnostics."
				qualifier="Read-only, run on demand."
				description="Saved read-only SQL. Every statement runs inside a READ ONLY transaction, and nothing runs until you press a button."
				actions={
					<>
						{ran.length > 1 && (
							<CopyButton text={everything} label="Copy everything" />
						)}
						<button
							type="button"
							className={btn.primary}
							disabled={running !== null}
							onClick={runAll}
						>
							<Play className="h-4 w-4" />
							{running === "*" ? "Running…" : "Run all"}
						</button>
					</>
				}
			/>

			<ErrorBanner error={error} />
			{loading && <Spinner />}

			{scripts.map((script) => {
				const result = results[script.slug];
				return (
					<Panel
						key={script.slug}
						title={script.name}
						hint={`${script.statements} statements · ${script.lines} lines`}
						action={
							<div className="flex gap-2">
								{result && <CopyButton text={asText(result)} label="Copy" />}
								<button
									type="button"
									className={btn.ghost}
									disabled={running !== null}
									onClick={() => runOne(script.slug)}
								>
									<Terminal className="h-4 w-4" />
									{running === script.slug ? "Running…" : "Run"}
								</button>
							</div>
						}
					>
						<p className="text-[12px] text-slate-500">{script.description}</p>

						{result?.error && (
							<p className="mt-3 text-[12px] font-medium text-danger">
								{result.error}
							</p>
						)}

						{result && !result.error && (
							<div className="mt-4 space-y-3">
								{result.statements.map((statement) => (
									<StatementView
										key={`${result.slug}-${statement.index}`}
										statement={statement}
									/>
								))}
								<p className="text-[11px] text-slate-400">
									finished in {result.ms} ms
								</p>
							</div>
						)}

						{!result && !loading && (
							<div className="mt-3">
								<Empty>Not run yet.</Empty>
							</div>
						)}
					</Panel>
				);
			})}
		</div>
	);
}
